import { FC, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { breakpoints } from '../../../theme/breakpoints'
import logoUrl from 'src/assets/images/logo.png'
import { I18n } from 'common'
import {
  backupSession,
  getCurrentSession,
  hasBackedUpSession,
  restoreBackedUpSession,
} from '../helper'
import { useUserStore } from '../../../store/user/UserStore'
import { redirectToAllianceConnect } from 'src/helpers/redirectToAllianceConnect'
import UserInfoServiceProvider from 'src/services/UserInfoServiceProvider'
import authManager from '../../../api/security/Auth'
import { UserAccessDetails } from 'src/api/user/interfaces'
import { Spinner } from 'react-bootstrap'

const Header: FC = () => {
  const user = useUserStore()

  const userLogoUrl = useMemo(() => user.userLogo || logoUrl, [user.userLogo])

  const [userAccessDetails, setUserAccessDetails] =
    useState<UserAccessDetails>(undefined)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
    UserInfoServiceProvider.getUserAccess(authManager.getUserId())
      .then((data) => {
        setUserAccessDetails(data)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  const handleCancel = (): void => {
    const session = getCurrentSession()
    restoreBackedUpSession()
    backupSession(session)
    window.location.reload()
  }

  const handleLogout = (): void => {
    user.signOut()
  }

  const redirectUser = (): void => {
    if (userAccessDetails) {
      redirectToAllianceConnect(userAccessDetails.url, true)
    }
  }

  return (
    <Wrapper>
      <SpaceBetween>
        <LogoAndTitle>
          <LogoImage src={userLogoUrl} alt="My place 4 parts logo" />
          <Title>
            <I18n name="organizationSelection" />
          </Title>
        </LogoAndTitle>

        <Buttons>
          {loading ? (
            <Spinner size="sm" style={{ margin: 'auto 0' }} />
          ) : (
            <Button onClick={() => redirectUser()}>
              <I18n name="dashboard" />
            </Button>
          )}
          {hasBackedUpSession() ? (
            <Button onClick={handleCancel}>
              <I18n name="cancel" />
            </Button>
          ) : null}

          <Button onClick={handleLogout}>
            <I18n name="logout" />
          </Button>
        </Buttons>
      </SpaceBetween>
      <MobileTitle>
        <I18n name="organizationSelection" />
      </MobileTitle>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const SpaceBetween = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`

const LogoAndTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  @media screen and (width < ${breakpoints.laptop}px) {
    gap: 24px;
  }
`

export const LogoImage = styled.img`
  width: 167px;
  height: 65px;
  @media screen and (width < ${breakpoints.tabletLarge}px) {
    width: 101.17px;
    height: 37.42px;
    margin-left: 0px;
  }
`

const Title = styled.div`
  font-family: Oswald;
  font-weight: 700;
  font-size: 28px;
  line-height: 48px;
  letter-spacing: 0px;
  margin: auto;
  @media screen and (width < ${breakpoints.tablet}px) {
    display: none;
  }
`

const MobileTitle = styled(Title)`
  display: none;
  @media screen and (width < ${breakpoints.tablet}px) {
    display: block;
    margin: auto 0;
  }
`

const Button = styled.div`
  font-family: Oswald;
  font-weight: 700;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0px;
  vertical-align: middle;
  text-transform: uppercase;
  margin: auto 0;
  cursor: pointer;
`

const Buttons = styled.div`
  display: flex;
  gap: 16px;
`

export default Header
